import { useState } from "react";
import { HeartHandshake, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PrayerRequest() {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error("Please share your prayer request."); return; }
    setSubmitting(true);
    try {
      await api.post("/prayer-requests", {
        message: message.trim(),
        name: anonymous ? null : name.trim(),
        anonymous,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit your request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 sm:px-8 pt-24 pb-24 text-center" data-testid="prayer-thanks">
        <div className="w-14 h-14 rounded-full bg-[#F3F0EA] flex items-center justify-center mx-auto">
          <Check className="w-7 h-7 text-[#9B7E4E]" strokeWidth={2} />
        </div>
        <h1 className="font-heading text-3xl text-[#2C2A29] tracking-tight mt-6">Your request is received</h1>
        <p className="font-decree text-lg text-[#4A4744] mt-3">
          It has been sent privately to our prayer team. Be encouraged — you are covered.
        </p>
        <button
          data-testid="prayer-submit-another"
          onClick={() => { setDone(false); setMessage(""); }}
          className="mt-8 px-6 py-3 border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300"
        >
          Submit another
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="prayer-page">
      <div className="flex items-center gap-3 mb-2">
        <HeartHandshake className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Prayer Request</p>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">We'd be honored to pray with you</h1>
      <p className="font-decree text-lg text-[#7A756E] mt-3">
        Share what's on your heart. Your request is private — it goes only to our prayer team.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div>
          <label className="font-body text-sm text-[#4A4744]">Your prayer request</label>
          <textarea
            data-testid="prayer-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            placeholder="Share your need, and how we can stand in agreement with you…"
            className="mt-2 w-full bg-transparent border border-[#D9D4CC] px-4 py-3 text-base text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300 resize-none"
          />
        </div>

        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="prayer-anon-toggle">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
            className="w-4 h-4 accent-[#9B7E4E]"
          />
          <span className="font-body text-sm text-[#4A4744]">Submit anonymously</span>
        </label>

        {!anonymous && (
          <div data-testid="prayer-name-field">
            <label className="font-body text-sm text-[#4A4744]">Your name <span className="text-[#A39C90]">(optional)</span></label>
            <input
              data-testid="prayer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="How should we address you?"
              className="mt-2 w-full bg-transparent border border-[#D9D4CC] px-4 py-3 text-base text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300"
            />
          </div>
        )}

        <button
          data-testid="prayer-submit"
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 px-8 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><HeartHandshake className="w-4 h-4" /> Send prayer request</>}
        </button>
      </form>
    </div>
  );
}
